import { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress } from '@mui/material'
import { RoomSubFieldDef, SubFormValues } from '@/types/roomSubInfo'
import { getRoomSubInfos, getUserRoomSubInfos, saveRoomSubInfos } from '@/services/roomService'
import { SubInfoForm } from '@/components/SubInfoForm'

interface RoomSubInfoRegistrationFormProps {
  roomId: string
  submitLabel?: string
  fieldSize?: 'small' | 'medium'
  onSaved: (values: SubFormValues, fieldDefs: RoomSubFieldDef[]) => void | Promise<void>
  /** ステップ遷移などで再マウントされても入力中の値を保持するための下書き値 */
  draftValues?: SubFormValues
  /** 入力値の変化を親に伝える（下書き保持用） */
  onDraftChange?: (values: SubFormValues) => void
}

export const RoomSubInfoRegistrationForm = ({
  roomId,
  submitLabel = '保存する',
  fieldSize = 'small',
  onSaved,
  draftValues,
  onDraftChange,
}: RoomSubInfoRegistrationFormProps) => {
  const [fieldDefs, setFieldDefs] = useState<RoomSubFieldDef[]>([])
  const [initialValues, setInitialValues] = useState<SubFormValues>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDefs = async () => {
      try {
        setLoading(true)
        setError(null)
        const [defs, currentValues] = await Promise.all([
          getRoomSubInfos(roomId),
          getUserRoomSubInfos(roomId),
        ])

        const mappedDefs = defs.map((d) => ({
            id: d.roomSubInfoId,
            roomSubInfoId: d.roomSubInfoId,
            label: d.propertyName,
            type: d.propertyType,
            required: d.required,
          }))

        const currentValueMap = new Map<string, string | null>(
          currentValues.map((v) => [v.roomSubInfoId, v.subValue]),
        )
        const mappedInitialValues: SubFormValues = {}
        mappedDefs.forEach((def) => {
          const raw = currentValueMap.get(def.roomSubInfoId ?? def.id)
          if (raw === undefined || raw === null) return

          if (def.type === 'checkbox') {
            mappedInitialValues[def.id] = raw === 'true' || raw === '1'
            return
          }

          mappedInitialValues[def.id] = raw
        })

        setFieldDefs(mappedDefs)
        // 下書き(入力中)の値があれば、保存済みの値より優先して復元する
        setInitialValues({ ...mappedInitialValues, ...(draftValues ?? {}) })
      } catch (fetchError) {
        console.error('[RoomSubInfoRegistrationForm] fetch error:', fetchError)
        setError('サブ情報定義の取得に失敗しました。時間をおいて再度お試しください。')
      } finally {
        setLoading(false)
      }
    }

    fetchDefs()
  }, [roomId])

  const handleComplete = async (values: SubFormValues) => {
    try {
      setError(null)
      const subInfos = fieldDefs.map((def) => ({
        roomSubInfoId: def.roomSubInfoId ?? def.id,
        subValue: values[def.id] ?? '',
      }))

      await saveRoomSubInfos({ roomId, subInfos })
      await onSaved(values, fieldDefs)
    } catch (saveError) {
      console.error('[RoomSubInfoRegistrationForm] save error:', saveError)
      setError('サブ情報の保存に失敗しました。時間をおいて再度お試しください。')
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <SubInfoForm
          fieldDefs={fieldDefs}
          initialValues={initialValues}
          onComplete={handleComplete}
          submitLabel={submitLabel}
          fieldSize={fieldSize}
          onValuesChange={onDraftChange}
        />
      )}
    </Box>
  )
}
